import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { VerifiedBadge } from '@/components/VerifiedBadge';
import { PasswordInput } from '@/components/PasswordInput';
import {
  Search, ShieldCheck, ShieldOff, Ban, Check, Eye, Send, Trash2, KeyRound,
  SquareCheck, Square
} from 'lucide-react';
import { Chevron, Pagination, InfoField, verificationStatusLabels } from './shared';

export function UsersTab({
  users, userSearch, setUserSearch, userPage, setUserPage, userPages, userTotal,
  selectedUsers, toggleSelectUser, selectAllUsers, bulkUserAction,
  toggleUserStatus, deleteUser, resetPassword, viewUserDetail, setEmailModal
}) {
  const [expandedId, setExpandedId] = useState(null);
  const [resetPasswordForm, setResetPasswordForm] = useState({});
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const handleResetPassword = async (userId) => {
    const pw = resetPasswordForm[userId];
    if (!pw || pw.length < 6) return;
    await resetPassword(userId, pw);
    setResetPasswordForm(prev => ({ ...prev, [userId]: '' }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input placeholder="Rechercher un utilisateur..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="pl-9" data-testid="user-search-input" />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={selectAllUsers} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300" data-testid="select-all-users">
            {selectedUsers.size === users.length && users.length > 0 ? <SquareCheck className="w-5 h-5 text-[#1F3F72]" /> : <Square className="w-5 h-5" />}
          </button>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">{userTotal} utilisateur{userTotal > 1 ? 's' : ''} — Page {userPage}/{userPages}</p>
        </div>
        {selectedUsers.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-[#1F3F72] dark:text-blue-300">{selectedUsers.size} selectionne(s)</span>
            <Button size="sm" variant="outline" onClick={() => bulkUserAction('suspend')} className="border-amber-300 text-amber-600 text-xs" data-testid="bulk-suspend-users"><Ban className="w-3 h-3 mr-1" /> Suspendre</Button>
            <Button size="sm" variant="outline" onClick={() => bulkUserAction('activate')} className="border-emerald-300 text-emerald-600 text-xs" data-testid="bulk-activate-users"><Check className="w-3 h-3 mr-1" /> Activer</Button>
            <Button size="sm" variant="outline" onClick={() => bulkUserAction('delete')} className="border-red-300 text-red-600 text-xs" data-testid="bulk-delete-users"><Trash2 className="w-3 h-3 mr-1" /> Supprimer</Button>
          </div>
        )}
      </div>
      <div className="space-y-3">
        {users.map((u) => (
          <div key={u.id} className="bg-white dark:bg-neutral-800 rounded-xl border dark:border-neutral-700 overflow-hidden" data-testid={`admin-user-${u.id}`}>
            <div className="p-4 flex items-center gap-3">
              <button onClick={(e) => { e.stopPropagation(); toggleSelectUser(u.id); }} className="text-neutral-400 hover:text-neutral-600 shrink-0">
                {selectedUsers.has(u.id) ? <SquareCheck className="w-5 h-5 text-[#1F3F72]" /> : <Square className="w-5 h-5" />}
              </button>
              <div className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => setExpandedId(expandedId === u.id ? null : u.id)}>
                <div className="w-10 h-10 rounded-full bg-[#1F3F72]/10 dark:bg-[#1F3F72]/25 flex items-center justify-center font-bold text-sm text-[#1F3F72] dark:text-[#93b4e0] shrink-0">
                  {u.first_name?.[0]}{u.last_name?.[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm text-neutral-900 dark:text-white truncate">{u.first_name} {u.last_name}</p>
                    {u.role === 'admin' && <Badge className="bg-purple-100 text-purple-700 text-[10px]">Admin</Badge>}
                    {u.verification_status === 'verified' && <VerifiedBadge size="xs" />}
                  </div>
                  <p className="text-xs text-neutral-500 truncate">{u.email}</p>
                </div>
                <div className="hidden sm:block text-right">
                  <p className="text-sm text-neutral-700 dark:text-neutral-300">{u.company?.name || '-'}</p>
                  <p className="text-xs text-neutral-400">{u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : ''}</p>
                </div>
                <Badge className={`text-[10px] shrink-0 ${u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{u.is_active ? 'Actif' : 'Suspendu'}</Badge>
                <Chevron open={expandedId === u.id} />
              </div>
            </div>
            {expandedId === u.id && (
              <div className="border-t dark:border-neutral-700 px-4 py-5 bg-neutral-50/50 dark:bg-neutral-850">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5 text-sm">
                  <InfoField label="Prenom" value={u.first_name || '-'} />
                  <InfoField label="Nom" value={u.last_name || '-'} />
                  <InfoField label="Email" value={u.email} />
                  <InfoField label="Entreprise" value={u.company?.name || '-'} />
                  <InfoField label="TVA" value={u.company?.vat_number || '-'} />
                  <InfoField label="Telephone" value={u.company?.phone || '-'} />
                  <InfoField label="Adresse" value={u.company?.address || '-'} />
                  <InfoField label="Pays" value={u.company?.country || '-'} />
                  <InfoField label="Inscrit le" value={u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'} />
                  <InfoField label="Role" value={u.role || 'user'} />
                  <InfoField label="Verification" value={verificationStatusLabels[u.verification_status] || 'Non verifie'} />
                </div>
                <div className="space-y-3 pt-3 border-t dark:border-neutral-700">
                  <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Actions</p>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => viewUserDetail(u.id)} data-testid={`view-detail-${u.id}`}>
                      <Eye className="w-4 h-4 mr-1" /> Details complets
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEmailModal(u.email)} data-testid={`email-user-${u.id}`}>
                      <Send className="w-4 h-4 mr-1" /> Email
                    </Button>
                    {u.role !== 'admin' && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => toggleUserStatus(u.id, u.is_active)} className={u.is_active ? 'border-red-300 text-red-600 hover:bg-red-50' : 'border-emerald-300 text-emerald-600 hover:bg-emerald-50'} data-testid={`toggle-user-${u.id}`}>
                          {u.is_active ? <><ShieldOff className="w-4 h-4 mr-1" /> Suspendre</> : <><ShieldCheck className="w-4 h-4 mr-1" /> Reactiver</>}
                        </Button>
                        {deleteConfirmId === u.id ? (
                          <div className="flex items-center gap-2">
                            <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white" onClick={() => { deleteUser(u.id); setDeleteConfirmId(null); }} data-testid={`confirm-delete-user-${u.id}`}><Trash2 className="w-4 h-4 mr-1" /> Confirmer</Button>
                            <Button size="sm" variant="ghost" onClick={() => setDeleteConfirmId(null)}>Annuler</Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => setDeleteConfirmId(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4 mr-1" /> Supprimer</Button>
                        )}
                      </>
                    )}
                  </div>
                  {u.role !== 'admin' && (
                    <div className="flex items-center gap-2 mt-2">
                      <KeyRound className="w-4 h-4 text-neutral-400 shrink-0" />
                      <PasswordInput placeholder="Nouveau mot de passe (min. 6)" value={resetPasswordForm[u.id] || ''} onChange={(e) => setResetPasswordForm(prev => ({ ...prev, [u.id]: e.target.value }))} className="max-w-[240px] text-sm" data-testid={`reset-pw-input-${u.id}`} />
                      <Button size="sm" variant="outline" onClick={() => handleResetPassword(u.id)} data-testid={`reset-pw-btn-${u.id}`}>Reinitialiser</Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <Pagination page={userPage} pages={userPages} onPageChange={setUserPage} testId="users" />
    </div>
  );
}
